<template>
  <div>
    <v-form ref="form" @submit.prevent="onSubmit" v-model="valid">
      <v-alert v-if="entityIsLocked" type="info">
        The organization's {{ entityName }}s are locked for editing. The form
        has been disabled.
      </v-alert>
      <div v-for="(attribute, index) in attributeList" :key="index">
        <div v-if="attribute">
          <v-alert type="error" v-if="!attribute.elementType"
            >Missing UI component for "{{ attribute.label }}"
          </v-alert>
          <component
            :attribute="attribute"
            :attributeName="attribute.name"
            :comboboxOptionList="attribute.comboboxOptionList"
            :conditionalList="attribute.conditionalList"
            :counter="attribute.maxLength"
            :disabledOverride="isPreviewMode ? true : null"
            :entity="entity"
            :entityName="entityName"
            :fkName="attribute.fkName"
            :helpText="attribute.helpText"
            :idAttributeName="primaryKeyName"
            :is="attribute.elementType"
            :isBatchEdit="isBatchEdit"
            :label="attribute.label"
            :minLength="attribute.minLength"
            :mode="mode"
            :multiple="attribute.multiple"
            :preHTML="attribute.preHTML"
            :required="isRequired(attribute)"
            :ruleList="getRuleList(attribute)"
            :valueDefault="attribute.valueDefault"
            :valueNo="attribute.valueNo"
            :valueYes="attribute.valueYes"
            @change="entity => $emit('change', entity)"
            @validate="validate"
            v-else
          ></component>
        </div>
      </div>

      <v-dialog v-model="cancelConfirmDialog" max-width="400" persistent>
        <v-card data-cy="entityForm_unsavedEditDialog">
          <v-card-title class="headline">Confirmation</v-card-title>
          <v-card-text>
            You have unsaved changes.<br />Are you sure you want to exit this
            form?
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn
              outlined
              class="mr-2"
              data-cy="entityForm_unsavedEditDialog_cancelBtn"
              @click="setCancelConfirmDialog(false)"
            >
              Cancel</v-btn
            >
            <v-btn
              color="warning elevation-0 font-weight-bold"
              data-cy="entityForm_unsavedEditDialog_exitBtn"
              @click="$emit('cancel')"
              >Exit</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <div v-if="!isPreviewMode">
        <p class="text-right">*: Required</p>
        <v-alert
          color="yellow darken-4"
          class="mt-4"
          data-cy="entityForm_unsaved_edit_message"
          dismissible
          outlined
          type="warning"
          v-model="isChanged"
          >You have unsaved changes in this form.</v-alert
        >
        <v-alert
          data-cy="entityForm_validation_error_message"
          dismissible
          outlined
          type="error"
          v-model="showValidationAlert"
          >Some fields have validation errors. Review errors above before
          resubmitting.</v-alert
        >
        <div class="d-flex justify-space-between">
          <v-btn
            @click="isChanged ? setCancelConfirmDialog(true) : $emit('cancel')"
            color="primary"
            data-cy="formCancel"
            outlined
            small
            >{{ btnTxtCancel }}</v-btn
          >
          <v-btn
            :disabled="entityIsLocked"
            @click="save"
            color="primary"
            data-cy="formSubmit"
            outlined
            small
            >{{ btnTxtSubmit }}</v-btn
          >
        </div>
      </div>
    </v-form>
  </div>
</template>

<script>
import { cloneDeep, isEqual } from "lodash";
import { mapActions, mapGetters } from "vuex";

import * as components from "./static";

export default {
  components: {
    ...components,
  },
  computed: {
    isPreviewMode() {
      return this.mode === "preview";
    },
    isChanged() {
      const { entity, initialEntityState } = this;

      return !isEqual(initialEntityState, entity);
    },
    entityIsLocked() {
      return !!this.entity.locked;
    },
    ...mapGetters({
      attributeFilter: "attribute/filter",
    }),
  },
  async created() {
    await Promise.all([this.attributeGetList({ reload: false })]);

    if (this.mode === "create") {
      const defaultValueMap = this.attributeList.reduce((acc, element) => {
        const { name, valueDefault } = element;

        if (!valueDefault) {
          return acc;
        }

        acc[name] = valueDefault;

        return acc;
      }, {});

      const entity = Object.assign(this.entity, defaultValueMap);

      this.$emit("change", entity);
    }

    this.initialEntityState = cloneDeep(this.entity);
  },
  data() {
    return {
      cancelConfirmDialog: false,
      initialEntityState: null,
      valid: undefined,
      showValidationAlert: false,
    };
  },
  methods: {
    getRuleList(attribute) {
      const isRequired = this.isRequired(attribute);
      if (isRequired) {
        return [
          function validateRequired(v) {
            return (
              (v !== undefined && v !== null && `${v}`.length > 0) ||
              (Array.isArray(v) && v.length > 0) ||
              `${attribute.label} is required`
            );
          },
        ];
      }
      return [];
    },
    isRequired(attribute) {
      const { mode } = this;

      // Make required if attribute definition has "required: true"
      if (attribute.required) {
        return true;
      }

      // Make required if attribute definition has "allowNull: false"
      if (attribute.allowNull === false) {
        return true;
      }

      // Make required if mode is create and attribute is required on create
      if (mode === "create" && attribute.requiredCreate) {
        return true;
      }

      // Make required if mode is update and attribute is required on update,
      if (mode === "update" && attribute.requiredUpdate) {
        return true;
      }

      // All other attributes are not required
      return false;
    },
    save() {
      const isValid = this.validate();

      if (!isValid) {
        this.showValidationAlert = true;
        return;
      }

      this.$emit(this.mode === "update" ? "submitUpdate" : "submitCreate");
    },
    setCancelConfirmDialog(val) {
      if (val !== true && val !== false) {
        console.error("setCancelConfirmDialog only accepts boolean values.");
        return;
      }

      this.cancelConfirmDialog = val;
    },
    validate() {
      return this.$refs.form.validate();
    },
    ...mapActions({
      attributeGetList: "attribute/list",
    }),
  },
  model: {
    prop: "entity",
    event: "change",
  },
  mounted() {
    // Need to reset and re-run validation after data loads
    this.$refs.form.resetValidation();
    this.$refs.form.validate();
  },
  props: {
    attributeList: {
      required: true,
      type: Array,
    },
    btnTxtSubmit: {
      default: "Submit",
      type: String,
    },
    btnTxtCancel: {
      default: "Cancel",
      type: String,
    },
    entity: {
      type: Object,
    },
    entityName: {
      required: true,
      type: String,
    },
    isBatchEdit: {
      default: false,
      type: Boolean,
    },
    mode: {
      default: "update",
      type: String,
      validator: mode => ["create", "update", "preview"].includes(mode),
    },
    primaryKeyName: {
      required: true,
      type: String,
    },
  },
};
</script>
